import { useState, useEffect, useRef, useCallback } from "react";
import { useFormBuilder } from "../hooks/useFormBuilder";
import { useParams } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import ConversationalFormRenderer from "../Components/ConversationalFormRenderer";
import { getInputStyle, getButtonStyle, getShadowStyle } from "../Components/formThemeStyles";

function PublicFormPage() {
    const { formId } = useParams();
    const { getPublicForm, submitPublicForm, loading, error } =
        useFormBuilder();

    const [form, setForm] = useState(null);
    const [formData, setFormData] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");
    const [notFound, setNotFound] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const recaptchaRef = useRef(null);

    const loadForm = useCallback(async () => {
        const data = await getPublicForm(formId);
        if (data) {
            setForm(data);
        } else {
            setNotFound(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formId]);

    useEffect(() => {
        loadForm();
    }, [loadForm]);

    // Load Google Fonts dynamically (form theme font)
    useEffect(() => {
        if (!form?.theme?.fontFamily) return;
        const font = form.theme.fontFamily;
        if (font === "Inter") return; // default, likely already loaded
        const existing = document.querySelector(
            `link[href*="${font.replace(/\s/g, "+")}"]`
        );
        if (existing) return;
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = `https://fonts.googleapis.com/css2?family=${font.replace(
            /\s/g,
            "+"
        )}:wght@300;400;500;600;700&display=swap`;
        document.head.appendChild(link);
    }, [form?.theme?.fontFamily]);

    // Inject workspace branding (CSS custom properties, custom CSS, favicon, font)
    useEffect(() => {
        const wb = form?.branding;
        if (!wb) return;

        // Favicon
        if (wb.faviconUrl) {
            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
                link = document.createElement("link");
                link.rel = "icon";
                document.head.appendChild(link);
            }
            link.href = wb.faviconUrl;
        }

        // Load workspace font from Google Fonts
        if (wb.fontFamily && wb.fontFamily !== "Inter") {
            const key = wb.fontFamily.replace(/\s/g, "+");
            if (!document.querySelector(`link[href*="${key}"]`)) {
                const fontLink = document.createElement("link");
                fontLink.rel = "stylesheet";
                fontLink.href = `https://fonts.googleapis.com/css2?family=${key}:wght@300;400;500;600;700&display=swap`;
                document.head.appendChild(fontLink);
            }
        }

        // CSS custom properties + custom CSS block
        let styleEl = document.getElementById("cfai-workspace-branding");
        if (!styleEl) {
            styleEl = document.createElement("style");
            styleEl.id = "cfai-workspace-branding";
            document.head.appendChild(styleEl);
        }
        styleEl.textContent = [
            `:root {`,
            wb.primaryColor    ? `  --form-primary: ${wb.primaryColor};` : "",
            wb.accentColor     ? `  --form-accent: ${wb.accentColor};` : "",
            wb.backgroundColor ? `  --form-bg: ${wb.backgroundColor};` : "",
            wb.textColor       ? `  --form-text: ${wb.textColor};` : "",
            wb.fontFamily      ? `  --form-font: '${wb.fontFamily}', sans-serif;` : "",
            `}`,
            wb.customCss || ""
        ].filter(Boolean).join("\n");

        // Update document title if companyName present
        if (wb.companyName && form?.title) {
            document.title = `${form.title} — ${wb.companyName}`;
        }

        return () => {
            // Cleanup on unmount
            const el = document.getElementById("cfai-workspace-branding");
            if (el) el.remove();
        };
    }, [form?.branding, form?.title]);

    const handleChange = (fieldName, value) => {
        setFormData({ ...formData, [fieldName]: value });
    };

    const handleCheckboxChange = (fieldName, option, checked) => {
        const current = formData[fieldName]
            ? formData[fieldName].split(", ")
            : [];
        let updated;
        if (checked) {
            updated = [...current, option];
        } else {
            updated = current.filter((v) => v !== option);
        }
        setFormData({ ...formData, [fieldName]: updated.join(", ") });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        let captchaToken = null;
        if (form.captchaEnabled && recaptchaRef.current) {
            captchaToken = recaptchaRef.current.getValue();
            if (!captchaToken) {
                setSubmitting(false);
                return;
            }
        }

        const result = await submitPublicForm(formId, formData, captchaToken);
        if (result) {
            setSubmitted(true);
            setSuccessMsg(
                result.message || "Thank you for your response!"
            );
        }
        setSubmitting(false);
    };

    if (loading && !form) {
        return (
            <div
                style={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "Inter, sans-serif",
                }}
            >
                <div style={{ textAlign: "center" }}>
                    <div
                        style={{
                            width: "40px",
                            height: "40px",
                            border: "3px solid #e5e7eb",
                            borderTopColor: "#6366f1",
                            borderRadius: "50%",
                            animation: "spin 0.8s linear infinite",
                            margin: "0 auto 16px",
                        }}
                    />
                    <p style={{ fontSize: "16px", color: "#6b7280" }}>
                        Loading form...
                    </p>
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
            </div>
        );
    }

    if (notFound) {
        return (
            <div
                style={{
                    minHeight: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "Inter, sans-serif",
                }}
            >
                <div
                    style={{
                        width: "64px",
                        height: "64px",
                        borderRadius: "50%",
                        backgroundColor: "#fef2f2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 16px",
                        fontSize: "28px",
                    }}
                >
                    &#10007;
                </div>
                <p
                    style={{
                        fontSize: "24px",
                        color: "#374151",
                        fontWeight: 600,
                    }}
                >
                    Form not found
                </p>
                <p style={{ color: "#6b7280", marginTop: "8px" }}>
                    This form may have been removed or is not published.
                </p>
                <button
                    onClick={() => window.location.reload()}
                    style={{
                        marginTop: "16px",
                        padding: "8px 20px",
                        backgroundColor: "#6366f1",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "14px",
                    }}
                >
                    Try Again
                </button>
            </div>
        );
    }

    const successPlacement = form?.successPlacement || "fullpage";

    // Full page success — replaces the entire form
    if (submitted && successPlacement === "fullpage") {
        return (
            <div
                style={{
                    minHeight: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily:
                        form?.theme?.fontFamily || "Inter, sans-serif",
                    backgroundColor:
                        form?.theme?.backgroundColor || "#ffffff",
                }}
            >
                <div
                    style={{
                        textAlign: "center",
                        maxWidth: "500px",
                        padding: "40px",
                    }}
                >
                    <div
                        style={{
                            width: "72px",
                            height: "72px",
                            borderRadius: "50%",
                            backgroundColor:
                                form?.theme?.primaryColor || "#6366f1",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 20px",
                            color: "white",
                            fontSize: "32px",
                            animation: "scaleIn 0.3s ease-out",
                        }}
                    >
                        &#10003;
                    </div>
                    <h2
                        style={{
                            fontSize: "24px",
                            fontWeight: 600,
                            color: form?.theme?.labelColor || "#374151",
                            marginBottom: "8px",
                        }}
                    >
                        {successMsg}
                    </h2>
                    <style>{`@keyframes scaleIn { from { transform: scale(0); } to { transform: scale(1); } }`}</style>
                </div>
                <BrandingFooter form={form} style={{ position: "fixed", bottom: "16px" }} />
            </div>
        );
    }

    // Modal success — rendered as an overlay in the main form view
    const renderModalSuccess = () => {
        if (!submitted || successPlacement !== "modal") return null;
        return (
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0,0,0,0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 9999,
                    animation: "fadeIn 0.2s ease-out",
                }}
                onClick={() => setSubmitted(false)}
            >
                <div
                    style={{
                        backgroundColor: form?.theme?.backgroundColor || "#ffffff",
                        borderRadius: `${form?.theme?.borderRadius || 12}px`,
                        padding: "40px",
                        maxWidth: "420px",
                        width: "90%",
                        textAlign: "center",
                        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                        animation: "scaleIn 0.3s ease-out",
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div
                        style={{
                            width: "64px",
                            height: "64px",
                            borderRadius: "50%",
                            backgroundColor: form?.theme?.primaryColor || "#6366f1",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 16px",
                            color: "white",
                            fontSize: "28px",
                        }}
                    >
                        &#10003;
                    </div>
                    <h2
                        style={{
                            fontSize: "20px",
                            fontWeight: 600,
                            color: form?.theme?.labelColor || "#374151",
                            marginBottom: "8px",
                        }}
                    >
                        {successMsg}
                    </h2>
                    <button
                        onClick={() => setSubmitted(false)}
                        style={{
                            marginTop: "16px",
                            padding: "8px 24px",
                            backgroundColor: form?.theme?.primaryColor || "#6366f1",
                            color: form?.theme?.buttonTextColor || "#ffffff",
                            border: "none",
                            borderRadius: `${form?.theme?.borderRadius || 8}px`,
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: 600,
                        }}
                    >
                        Close
                    </button>
                </div>
                <style>{`
                    @keyframes scaleIn { from { transform: scale(0.8); opacity: 0; } to { transform: scale(1); opacity: 1; } }
                    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                `}</style>
            </div>
        );
    };

    if (!form) return null;

    if (form.display_mode === "conversational") {
        return <ConversationalFormRenderer form={form} formId={formId} />;
    }

    const theme = form.theme || {};
    const primaryColor = theme.primaryColor || "#6366f1";
    const bgColor = theme.backgroundColor || "#ffffff";
    const fontFamily = theme.fontFamily || "Inter, sans-serif";
    const containerWidth = theme.containerWidth || 600;
    const padding = theme.padding || 32;
    const fieldSpacing = theme.fieldSpacing || 16;
    const captchaSiteKey =
        form.recaptchaSiteKey || process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    return (
        <div
            style={{
                minHeight: "100vh",
                backgroundColor: bgColor,
                fontFamily: fontFamily,
                padding: "40px 16px",
            }}
        >
            <div
                style={{
                    maxWidth: `${containerWidth}px`,
                    margin: "0 auto",
                    backgroundColor:
                        theme.templateId === "dark" ? theme.inputBgColor || "#16213e" : "#ffffff",
                    borderRadius: `${theme.borderRadius || 12}px`,
                    boxShadow: getShadowStyle(theme.shadow),
                    padding: `${padding}px`,
                }}
            >
                {form.logo && (
                    <img
                        src={form.logo}
                        alt="Logo"
                        style={{
                            height: "48px",
                            marginBottom: "16px",
                        }}
                    />
                )}

                <h1
                    style={{
                        fontSize: "24px",
                        fontWeight: 700,
                        color: primaryColor,
                        marginBottom: "4px",
                    }}
                >
                    {form.title}
                </h1>

                {form.description && (
                    <p
                        style={{
                            color: theme.labelColor || "#6b7280",
                            marginBottom: "24px",
                            fontSize: "15px",
                        }}
                    >
                        {form.description}
                    </p>
                )}

                {/* Inline success banner */}
                {submitted && successPlacement === "inline" && (
                    <div
                        style={{
                            padding: "16px 20px",
                            backgroundColor: `${primaryColor}15`,
                            border: `1px solid ${primaryColor}40`,
                            borderRadius: `${theme.borderRadius || 8}px`,
                            marginBottom: `${fieldSpacing}px`,
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            animation: "fadeIn 0.3s ease-out",
                        }}
                    >
                        <div
                            style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                backgroundColor: primaryColor,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontSize: "16px",
                                flexShrink: 0,
                            }}
                        >
                            &#10003;
                        </div>
                        <span
                            style={{
                                color: theme.labelColor || "#374151",
                                fontSize: `${theme.fontSize || 14}px`,
                                fontWeight: 500,
                            }}
                        >
                            {successMsg}
                        </span>
                        <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    {form.fields.map((field) => (
                        <div
                            key={field.fieldId || field.name}
                            style={{ marginBottom: `${fieldSpacing}px` }}
                        >
                            <label
                                style={{
                                    display: "block",
                                    fontSize: `${theme.fontSize || 14}px`,
                                    fontWeight: 600,
                                    color: theme.labelColor || "#374151",
                                    marginBottom: "4px",
                                }}
                            >
                                {field.label}
                                {field.isRequired && (
                                    <span
                                        style={{
                                            color: "#ef4444",
                                            marginLeft: "2px",
                                        }}
                                    >
                                        *
                                    </span>
                                )}
                            </label>

                            {field.type === "textarea" ? (
                                <textarea
                                    style={getInputStyle(theme)}
                                    placeholder={field.placeholder}
                                    required={field.isRequired}
                                    rows={3}
                                    onChange={(e) =>
                                        handleChange(
                                            field.name,
                                            e.target.value
                                        )
                                    }
                                />
                            ) : field.type === "select" ? (
                                <select
                                    style={getInputStyle(theme)}
                                    required={field.isRequired}
                                    onChange={(e) =>
                                        handleChange(
                                            field.name,
                                            e.target.value
                                        )
                                    }
                                    defaultValue=""
                                >
                                    <option value="" disabled>
                                        {field.placeholder || "Select..."}
                                    </option>
                                    {(field.options || []).map((opt, i) => (
                                        <option key={i} value={opt}>
                                            {opt}
                                        </option>
                                    ))}
                                </select>
                            ) : field.type === "radio" ? (
                                <div>
                                    {(field.options || []).map((opt, i) => (
                                        <label
                                            key={i}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                fontSize: `${theme.fontSize || 14}px`,
                                                marginBottom: "4px",
                                                cursor: "pointer",
                                                color: theme.labelColor || "#374151",
                                                fontWeight: "normal",
                                            }}
                                        >
                                            <input
                                                type="radio"
                                                name={field.name}
                                                value={opt}
                                                required={field.isRequired}
                                                onChange={(e) =>
                                                    handleChange(
                                                        field.name,
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            {opt}
                                        </label>
                                    ))}
                                </div>
                            ) : field.type === "checkbox" ? (
                                <div>
                                    {(field.options || []).length > 0 ? (
                                        field.options.map((opt, i) => (
                                            <label
                                                key={i}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px",
                                                    fontSize: `${theme.fontSize || 14}px`,
                                                    marginBottom: "4px",
                                                    cursor: "pointer",
                                                    color: theme.labelColor || "#374151",
                                                    fontWeight: "normal",
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    onChange={(e) =>
                                                        handleCheckboxChange(
                                                            field.name,
                                                            opt,
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                                {opt}
                                            </label>
                                        ))
                                    ) : (
                                        <label
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                                fontSize: `${theme.fontSize || 14}px`,
                                                cursor: "pointer",
                                                color: theme.labelColor || "#374151",
                                                fontWeight: "normal",
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                onChange={(e) =>
                                                    handleChange(
                                                        field.name,
                                                        e.target.checked
                                                            ? "Yes"
                                                            : "No"
                                                    )
                                                }
                                            />
                                            {field.label}
                                        </label>
                                    )}
                                </div>
                            ) : (
                                <input
                                    type={field.type || "text"}
                                    style={getInputStyle(theme)}
                                    placeholder={field.placeholder}
                                    required={field.isRequired}
                                    onChange={(e) =>
                                        handleChange(
                                            field.name,
                                            e.target.value
                                        )
                                    }
                                />
                            )}
                        </div>
                    ))}

                    {form.captchaEnabled && captchaSiteKey && (
                        <div style={{ marginBottom: `${fieldSpacing}px` }}>
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey={captchaSiteKey}
                            />
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={submitting || (submitted && successPlacement !== "fullpage")}
                        style={getButtonStyle(theme, submitting || (submitted && successPlacement !== "fullpage"))}
                    >
                        {submitting
                            ? "Submitting..."
                            : submitted && successPlacement !== "fullpage"
                            ? "Submitted"
                            : form.submitButtonText || "Submit"}
                    </button>

                    {error && (
                        <p
                            style={{
                                color: "#ef4444",
                                marginTop: "12px",
                                fontSize: "14px",
                            }}
                        >
                            {error}
                        </p>
                    )}
                </form>
            </div>

            <BrandingFooter form={form} style={{ textAlign: "center", marginTop: "24px" }} />

            {renderModalSuccess()}
        </div>
    );
}

// Renders "Powered by" footer or custom footer text, or nothing when branding is removed.
function BrandingFooter({ form, style }) {
    const wb = form?.branding;
    if (form?.showBranding === false) {
        // Branding removed — show custom footer text if provided
        if (wb?.footerText) {
            return (
                <div style={{ textAlign: "center", ...style }}>
                    <span style={{ color: "#9ca3af", fontSize: "13px" }}>{wb.footerText}</span>
                </div>
            );
        }
        return null;
    }
    return (
        <div style={{ textAlign: "center", ...style }}>
            <a
                href="https://contentflashai.com"
                style={{ color: "#9ca3af", fontSize: "13px", textDecoration: "none" }}
            >
                Powered by Content Flash AI
            </a>
        </div>
    );
}

export default PublicFormPage;
